import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { ConfirmButton, Field, ImageField, SaveButton, SortableList, inputCls } from "../../components/admin/fields";

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

export default function PressAdminPage() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/press").then((res) => setItems(res.data || [])).catch(() => {});
  useEffect(load, []);

  const fetchPreview = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/press/fetch-preview", { url });
      setDraft({ url, publication: data.publication || "", headline: data.headline || "", thumbnail: data.thumbnail || "", date: new Date().toISOString().slice(0, 10) });
    } catch (e) {
      setError(formatApiError(e));
      setDraft({ url, publication: "", headline: "", thumbnail: "", date: new Date().toISOString().slice(0, 10) });
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/admin/press", { ...draft, date: draft.date ? new Date(draft.date).toISOString() : null });
      setDraft(null);
      setUrl("");
      load();
    } finally {
      setBusy(false);
    }
  };

  const updateItem = async (item) => {
    await api.put(`/admin/press/${item.id}`, { ...item, date: item.date ? new Date(item.date).toISOString() : null });
    load();
  };

  return (
    <div className="max-w-3xl" data-testid="admin-press">
      <h1 className="h-condensed text-4xl">Press</h1>

      <section className="mt-8 border border-charcoal p-6">
        <h2 className="eyebrow text-[0.65rem]">Add press item by URL</h2>
        <div className="mt-4 flex gap-3">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://publication.com/article…"
            className={inputCls}
            data-testid="press-url-input"
          />
          <button type="button" onClick={fetchPreview} disabled={busy || !url} className="shrink-0 border border-charcoal bg-charcoal px-5 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em] text-offwhite disabled:opacity-50" data-testid="press-fetch-button">
            {busy ? "Fetching…" : "Fetch preview"}
          </button>
        </div>
        {error && <p className="mt-3 text-xs" role="alert" data-testid="press-fetch-error">{error}</p>}

        {draft && (
          <div className="mt-6 space-y-5 border-t border-charcoal pt-6" data-testid="press-preview-form">
            <p className="text-xs font-light opacity-60">Check the fetched details, edit anything, then save.</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Publication name">
                <input value={draft.publication} onChange={(e) => setDraft({ ...draft, publication: e.target.value })} className={inputCls} data-testid="press-publication" />
              </Field>
              <Field label="Date">
                <input type="date" value={toDateInput(draft.date)} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className={inputCls} data-testid="press-date" />
              </Field>
            </div>
            <Field label="Headline">
              <input value={draft.headline} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} className={inputCls} data-testid="press-headline" />
            </Field>
            <ImageField label="Thumbnail" value={draft.thumbnail} onChange={(v) => setDraft({ ...draft, thumbnail: v })} testid="press-thumbnail" />
            <div className="flex gap-4">
              <SaveButton busy={busy} dirty testid="press-save-button" />
              <span onClick={saveDraft} className="hidden" />
              <button type="button" onClick={saveDraft} className="border border-charcoal bg-charcoal px-6 py-2.5 font-condensed text-xs font-semibold uppercase tracking-[0.14em] text-offwhite" data-testid="press-confirm-save">
                Save press item
              </button>
              <button type="button" onClick={() => setDraft(null)} className="link-arrow text-xs" data-testid="press-cancel">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="mt-10 border-t border-charcoal">
        <SortableList
          items={items}
          onChange={setItems}
          onPersist={() => api.post("/admin/press/reorder", { ids: items.map((i) => i.id) })}
          keyFn={(i) => i.id}
          testid="press-list"
          renderItem={(item, idx) => (
            <div className="grid gap-3 border-b border-charcoal py-4 sm:grid-cols-[64px_1fr_auto]" data-testid={`press-row-${idx}`}>
              {item.thumbnail ? (
                <img src={item.thumbnail} alt="" className="h-12 w-16 object-cover" />
              ) : (
                <span className="flex h-12 w-16 items-center justify-center bg-stone-40 text-[0.6rem]">No image</span>
              )}
              <div className="space-y-2">
                <div className="grid gap-2 sm:grid-cols-[160px_1fr_140px]">
                  <input value={item.publication} onChange={(e) => setItems(items.map((x) => (x.id === item.id ? { ...x, publication: e.target.value } : x)))} className={inputCls} data-testid={`press-row-publication-${idx}`} />
                  <input value={item.headline} onChange={(e) => setItems(items.map((x) => (x.id === item.id ? { ...x, headline: e.target.value } : x)))} className={inputCls} data-testid={`press-row-headline-${idx}`} />
                  <input type="date" value={toDateInput(item.date)} onChange={(e) => setItems(items.map((x) => (x.id === item.id ? { ...x, date: e.target.value } : x)))} className={inputCls} data-testid={`press-row-date-${idx}`} />
                </div>
                <input value={item.url} onChange={(e) => setItems(items.map((x) => (x.id === item.id ? { ...x, url: e.target.value } : x)))} className={inputCls} data-testid={`press-row-url-${idx}`} />
              </div>
              <div className="flex items-start gap-3">
                <button type="button" onClick={() => updateItem(item)} className="link-arrow text-xs" data-testid={`press-row-save-${idx}`}>
                  Save
                </button>
                <ConfirmButton
                  label="Delete"
                  testid={`press-row-delete-${idx}`}
                  onConfirm={async () => {
                    await api.delete(`/admin/press/${item.id}`);
                    load();
                  }}
                />
              </div>
            </div>
          )}
        />
        {items.length === 0 && <p className="py-8 text-sm font-light opacity-60">No press items yet.</p>}
      </div>
    </div>
  );
}
